/**
 * AccountActions — account mutation operations extracted from AuthSession.
 *
 * Owns phone submission, account deletion, password changes, profile updates,
 * and email changes. Each method guards via the lazy deps accessor, performs
 * the service call, dispatches observability events, and returns a normalized result.
 *
 * Follows the same composition pattern as PendingActionManager and
 * SocialFlowCoordinator: AuthSession constructs this with lazy accessors
 * and delegates to it.
 */

import type { User } from '../../types/user';
import type { SocialProvider } from '../../types/results';
import type { AuthSessionAction, AuthSessionInternalDeps } from './types';
import { authCompleteEffect, userUpdatedEffect } from './effects';
import { extractErrorMessage } from '../../shared/extract-error-message';

type Dispatch = (action: AuthSessionAction) => void;
type Result = { ok: true } | { ok: false; error: string };

export class AccountActions {
  constructor(
    private readonly getDeps: () => AuthSessionInternalDeps,
    private readonly dispatch: Dispatch,
    private readonly getUser: () => User | null,
  ) {}

  async submitPhone(phone: string, profile?: { firstName: string; lastName: string }): Promise<Result> {
    const deps = this.getDeps();
    const currentUser = this.getUser();
    const userId = currentUser?.id ?? '';

    try {
      const result = await deps.service.submitPhone(userId, phone, profile);
      if (result && 'user' in result && result.user) {
        deps.observability.dispatch({ type: 'submit_phone_success' });
        this.emitPhoneCollectionComplete(result.user, deps);
        authCompleteEffect(result.user, this.dispatch, deps);
        return { ok: true };
      }
      if (currentUser) {
        deps.sessionStore.dispatch({ type: 'CLEAR_PENDING_ERROR' });
        deps.sessionStore.dispatch({ type: 'CLEAR_NEEDS_PHONE' });
        deps.observability.dispatch({ type: 'submit_phone_success' });
        userUpdatedEffect({ ...currentUser, homePhone: phone }, this.dispatch, deps);
        return { ok: true };
      }
      return { ok: false, error: 'Phone submitted but session could not be established.' };
    } catch (err) {
      deps.observability.dispatch({ type: 'submit_phone_failure', error: err });
      return { ok: false, error: extractErrorMessage(err) };
    }
  }

  async deleteAccount(): Promise<Result> {
    const deps = this.getDeps();
    let failed: { error: unknown } | null = null;

    try {
      await deps.service.deleteAccount();
    } catch (err) {
      failed = { error: err };
    }

    deps.persistence.dispatch({ type: 'CLEAR' });
    deps.sessionStore.dispatch({ type: 'CLEAR_PENDING_ERROR' });
    deps.sessionStore.dispatch({ type: 'CLEAR_NEEDS_PHONE' });
    this.dispatch({ type: 'SESSION_CLEARED' });

    if (failed) {
      deps.observability.dispatch({ type: 'delete_account_failure', error: failed.error });
      return { ok: false, error: extractErrorMessage(failed.error) };
    }

    deps.observability.dispatch({ type: 'delete_account_success' });
    return { ok: true };
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<Result> {
    const deps = this.getDeps();
    try {
      const result = await deps.service.changePassword(currentPassword, newPassword);
      if (result.error) return { ok: false, error: result.error };
      deps.observability.dispatch({ type: 'change_password_success' });
      return { ok: true };
    } catch (err) {
      deps.observability.dispatch({ type: 'change_password_failure', error: err });
      return { ok: false, error: extractErrorMessage(err) };
    }
  }

  async updateProfile(params: User): Promise<Result> {
    const deps = this.getDeps();
    return this.normalizeServiceResult(deps.service.updateProfile(params));
  }

  async updateEmail(
    currentUser: User,
    newEmail: string,
    retypeEmail: string,
    password?: string,
    passwordConfirmation?: string,
  ): Promise<Result> {
    const deps = this.getDeps();
    return this.normalizeServiceResult(
      deps.service.updateEmail(currentUser, newEmail, retypeEmail, password, passwordConfirmation),
    );
  }

  async confirmEmailUpdate(code: string): Promise<Result> {
    const deps = this.getDeps();
    return this.normalizeServiceResult(deps.service.confirmEmailUpdate(code));
  }

  private async normalizeServiceResult(
    promise: Promise<{ success: true } | { error: string }>,
  ): Promise<Result> {
    const result = await promise;
    return 'error' in result ? { ok: false, error: result.error } : { ok: true };
  }

  private emitPhoneCollectionComplete(user: User, deps: AuthSessionInternalDeps): void {
    const pending = deps.sessionStore.getSocialPending();
    if (!pending?.provider) return;
    deps.eventBus.emit({
      type: 'social_phone_collection_complete',
      user,
      provider: pending.provider as SocialProvider,
    });
  }
}
